import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
  Progress,
  Spinner,
  Row,
  Col,
  Card,
  Input,
  Label,
} from "reactstrap";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import {
  FaFileExcel,
  FaUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaDownload,
  FaTrash,
  FaEye,
  FaUserMinus,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { ApiCreateUsuarioNavidad } from "../../../api/navidad";
import ExcelPreview from "../../../components/admin/navidad/ExcelPreview";

const ExcelUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [processResult, setProcessResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [año, setAño] = useState(new Date().getFullYear());
  const [processingProgress, setProcessingProgress] = useState({
    current: 0,
    total: 0,
    success: 0,
    errors: 0,
    currentUser: null,
  });
  const [detectedColumns, setDetectedColumns] = useState({
    usuarioColumn: null,
    codeColumn: null,
    nombresColumn: null,
    apellidosColumn: null,
  });

  // Configuración del dropzone
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setFile(file);
      setExcelData(null);
      setValidationErrors([]);
      setProcessResult(null);
      validateExcelFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    multiple: false,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  // Validar archivo Excel
  const validateExcelFile = async (file) => {
    setIsValidating(true);
    setValidationErrors([]);
    setValidationResults([]);

    try {
      const data = await readExcelFile(file);
      const { errors, validationResults: results } = validateExcelData(data);

      if (errors.length === 0) {
        setExcelData(data);
        setValidationResults(results);
        const firstRow = data[0];
        const columnVariations = {
          Usuario: [
            "Usuario",
            "usuario",
            "USUARIO",
            "Cedula",
            "cedula",
            "CÉDULA",
            "cédula",
            "CEDULA",
            "CI",
            "ci",
            "C.I.",
            "c.i.",
          ],
          Code: [
            "Code",
            "code",
            "CODIGO",
            "codigo",
            "Código",
            "código",
            "Codigo",
            "COD",
            "cod",
            "Cod",
          ],
          Apellidos: [
            "Apellidos",
            "apellidos",
            "APELLIDOS",
            "Apellido",
            "apellido",
            "APELLIDO",
          ],
          Nombres: [
            "Nombres",
            "nombres",
            "NOMBRES",
            "Nombre",
            "nombre",
            "NOMBRE",
          ],
        };
        let usuarioColumn = null;
        let codeColumn = null;
        let apellidosColumn = null;
        let nombresColumn = null;
        Object.keys(firstRow).forEach((key) => {
          const k = key?.trim?.() ?? key;
          if (columnVariations.Usuario.includes(k)) usuarioColumn = key;
          if (columnVariations.Code.includes(k)) codeColumn = key;
          if (columnVariations.Apellidos.includes(k)) apellidosColumn = key;
          if (columnVariations.Nombres.includes(k)) nombresColumn = key;
        });
        setDetectedColumns({
          usuarioColumn,
          codeColumn,
          nombresColumn,
          apellidosColumn,
        });
        const validCount = results.filter((r) => r.isValid).length;
        const invalidCount = results.filter((r) => !r.isValid).length;
        toast.success(
          `Archivo validado: ${validCount} válidos, ${invalidCount} con errores`
        );
      } else {
        setValidationErrors(errors);
        toast.error("Errores encontrados en el archivo");
      }
    } catch (error) {
      setValidationErrors([`Error al leer el archivo: ${error.message}`]);
      toast.error("Error al procesar el archivo");
    } finally {
      setIsValidating(false);
    }
  };

  // Leer archivo Excel
  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Error al leer el archivo"));
      reader.readAsArrayBuffer(file);
    });
  };

  // Validar datos del Excel
  const validateExcelData = (data) => {
    const errors = [];
    const validationResults = [];

    if (!data || data.length === 0) {
      errors.push("El archivo Excel está vacío");
      return { errors, validationResults };
    }

    // Verificar columnas requeridas
    const firstRow = data[0];
    const columnVariations = {
      Usuario: [
        "Usuario",
        "usuario",
        "USUARIO",
        "Cedula",
        "cedula",
        "CÉDULA",
        "cédula",
        "CEDULA",
        "CI",
        "ci",
        "C.I.",
        "c.i.",
      ],
      Code: [
        "Code",
        "code",
        "CODIGO",
        "codigo",
        "Código",
        "código",
        "Codigo",
        "codigo",
        "COD",
        "cod",
        "Cod",
      ],
      Apellidos: [
        "Apellidos",
        "apellidos",
        "APELLIDOS",
        "Apellido",
        "apellido",
        "APELLIDO",
      ],
      Nombres: ["Nombres", "nombres", "NOMBRES", "Nombre", "nombre", "NOMBRE"],
    };

    let usuarioColumn = null;
    let codeColumn = null;
    let apellidosColumn = null;
    let nombresColumn = null;

    // Buscar columnas por variaciones
    Object.keys(firstRow).forEach((key) => {
      const k = key?.trim?.() ?? key;
      if (columnVariations.Usuario.includes(k)) usuarioColumn = key;
      if (columnVariations.Code.includes(k)) codeColumn = key;
      if (columnVariations.Apellidos.includes(k)) apellidosColumn = key;
      if (columnVariations.Nombres.includes(k)) nombresColumn = key;
    });

    if (!usuarioColumn && !codeColumn) {
      errors.push(
        'Debe existir al menos una de las columnas: "Usuario/Cedula" o "Code" (o sus variaciones)'
      );
    }

    if (!apellidosColumn) {
      errors.push('No se encontró la columna "Apellidos" (o sus variaciones)');
    }

    if (!nombresColumn) {
      errors.push('No se encontró la columna "Nombres" (o sus variaciones)');
    }

    if (errors.length > 0) {
      return { errors, validationResults };
    }

    // Validar cada fila individualmente
    data.forEach((row, index) => {
      const rowErrors = [];
      const usuario = usuarioColumn ? row[usuarioColumn] || null : null;
      const code = codeColumn ? row[codeColumn] || null : null;
      const apellidos = row[apellidosColumn];
      const nombres = row[nombresColumn];

      // Validar que tenga al menos usuario O code
      // Manejar casos donde el valor puede ser undefined, null, o cadena vacía
      const usuarioStr = usuario != null ? String(usuario).trim() : "";
      const codeStr = code != null ? String(code).trim() : "";

      const hasUsuario = usuarioStr !== "";
      const hasCode = codeStr !== "";

      if (!hasUsuario && !hasCode) {
        rowErrors.push("Debe tener al menos Usuario/Cédula o Code");
      }

      if (!apellidos || apellidos.toString().trim() === "") {
        rowErrors.push("Apellidos vacíos o inválidos");
      }

      if (!nombres || nombres.toString().trim() === "") {
        rowErrors.push("Nombres vacíos o inválidos");
      }

      validationResults.push({
        rowNumber: index + 2,
        data: row,
        isValid: rowErrors.length === 0,
        errors: rowErrors,
        observaciones:
          rowErrors.length > 0 ? rowErrors.join(", ") : "Registro válido",
      });
    });

    return { errors, validationResults };
  };

  const handleProcessFile = async () => {
    if (!file || !excelData || !validationResults.length) return;

    setIsProcessing(true);
    setProcessResult(null);

    try {
      // Filtrar solo los registros válidos
      const validRecords = validationResults.filter((r) => r.isValid);

      if (validRecords.length === 0) {
        toast.error("No hay registros válidos para procesar");
        setIsProcessing(false);
        return;
      }

      setProcessingProgress({
        current: 0,
        total: validRecords.length,
        success: 0,
        errors: 0,
        currentUser: null,
      });

      const errors = [];
      let successCount = 0;
      let skippedCount = 0;

      // Obtener columnas del primer registro válido
      const firstValidRecord = validRecords[0].data;
      const usuarioColumn = Object.keys(firstValidRecord).find((key) =>
        [
          "Usuario",
          "usuario",
          "USUARIO",
          "Cedula",
          "cedula",
          "CÉDULA",
          "cédula",
          "CEDULA",
          "CI",
          "ci",
          "C.I.",
          "c.i.",
        ].includes(key?.trim?.() ?? key)
      );
      const codeColumn = Object.keys(firstValidRecord).find((key) =>
        [
          "Code",
          "code",
          "CODIGO",
          "codigo",
          "Código",
          "código",
          "Codigo",
          "COD",
          "cod",
          "Cod",
        ].includes(key?.trim?.() ?? key)
      );
      const nombresColumn = Object.keys(firstValidRecord).find((key) =>
        [
          "Nombres",
          "nombres",
          "NOMBRES",
          "Nombre",
          "nombre",
          "NOMBRE",
        ].includes(key?.trim?.() ?? key)
      );
      const apellidosColumn = Object.keys(firstValidRecord).find((key) =>
        [
          "Apellidos",
          "apellidos",
          "APELLIDOS",
          "Apellido",
          "apellido",
          "APELLIDO",
        ].includes(key?.trim?.() ?? key)
      );
      const cargoColumn = Object.keys(firstValidRecord).find((key) =>
        ["Cargo", "cargo", "CARGO", "Cargos", "cargos", "CARGOS"].includes(
          key?.trim?.() ?? key
        )
      );
      const areaColumn = Object.keys(firstValidRecord).find((key) =>
        [
          "Area",
          "area",
          "AREA",
          "Área",
          "área",
          "ÁREA",
          "Areas",
          "areas",
          "AREAS",
        ].includes(key?.trim?.() ?? key)
      );
      const emailColumn = Object.keys(firstValidRecord).find((key) =>
        [
          "Email",
          "email",
          "EMAIL",
          "Correo",
          "correo",
          "CORREO",
          "E-mail",
          "e-mail",
          "E-MAIL",
        ].includes(key?.trim?.() ?? key)
      );

      // Procesar cada registro de uno en uno
      for (let i = 0; i < validRecords.length; i++) {
        const record = validRecords[i];
        const rowData = record.data;

        const usuario =
          usuarioColumn && rowData[usuarioColumn]
            ? rowData[usuarioColumn].toString().trim()
            : null;
        const code =
          codeColumn && rowData[codeColumn]
            ? rowData[codeColumn].toString().trim()
            : null;
        const nombres = rowData[nombresColumn]?.toString().trim() || null;
        const apellidos = rowData[apellidosColumn]?.toString().trim() || null;
        const cargo =
          cargoColumn && rowData[cargoColumn]
            ? rowData[cargoColumn].toString().trim()
            : null;
        const area =
          areaColumn && rowData[areaColumn]
            ? rowData[areaColumn].toString().trim()
            : null;
        const correo =
          emailColumn && rowData[emailColumn]
            ? rowData[emailColumn].toString().trim()
            : null;

        // Actualizar progreso
        setProcessingProgress({
          current: i + 1,
          total: validRecords.length,
          success: successCount,
          errors: errors.length,
          currentUser: `${nombres} ${apellidos}`,
        });

        try {
          // Preparar datos, solo incluir code si tiene valor
          const userData = {
            usuario,
            nombres,
            apellidos,
            año: año, // Incluir el año seleccionado
          };

          // Solo incluir code si tiene un valor válido
          if (code && code.trim() !== "") {
            userData.code = code.trim();
          }

          // Incluir campos opcionales solo si tienen valor
          if (cargo && cargo.trim() !== "") {
            userData.cargo = cargo.trim();
          }
          if (area && area.trim() !== "") {
            userData.area = area.trim();
          }
          if (correo && correo.trim() !== "") {
            userData.correo = correo.trim();
          }

          const result = await ApiCreateUsuarioNavidad(userData);

          if (result.sms === "ok") {
            successCount++;
          } else {
            // Si el error es que ya existe, contarlo como skipped
            if (result.mensaje && result.mensaje.includes("Ya existe")) {
              skippedCount++;
            } else {
              errors.push(
                `Fila ${record.rowNumber}: ${
                  result.mensaje || "Error desconocido"
                }`
              );
            }
          }
        } catch (error) {
          errors.push(
            `Fila ${record.rowNumber}: ${error.message || "Error al procesar"}`
          );
        }

        // Pequeña pausa para no sobrecargar el servidor
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Actualizar progreso final
      setProcessingProgress({
        current: validRecords.length,
        total: validRecords.length,
        success: successCount,
        errors: errors.length,
        currentUser: null,
      });

      // Mostrar resultado
      const resultMessage = `Procesamiento completado: ${successCount} creados, ${skippedCount} omitidos (ya existían), ${errors.length} errores`;

      setProcessResult({
        sms: errors.length === 0 ? "ok" : "partial",
        mensaje: resultMessage,
        data: {
          createdUsers: successCount,
          skippedUsers: skippedCount,
          errors: errors,
        },
      });

      if (errors.length === 0) {
        toast.success(resultMessage);
        setShowPreview(false);
        onSuccess();
      } else {
        toast.warning(resultMessage);
      }
    } catch (error) {
      toast.error("Error al procesar el archivo");
      console.error("Error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Limpiar datos
  const handleClear = () => {
    setFile(null);
    setExcelData(null);
    setValidationErrors([]);
    setValidationResults([]);
    setProcessResult(null);
    setShowPreview(false);
    setCurrentPage(1);
    setAño(new Date().getFullYear());
    setDetectedColumns({
      usuarioColumn: null,
      codeColumn: null,
      nombresColumn: null,
      apellidosColumn: null,
    });
  };

  // Descargar plantilla
  const downloadTemplate = () => {
    const templateData = [
      {
        Usuario: "1234567890",
        Code: "EMP001",
        Apellidos: "GARCIA",
        Nombres: "JUAN CARLOS",
        Cargo: "GERENTE",
        Area: "ADMINISTRACION",
        Email: "juan.garcia@empresa.com",
      },
      {
        Usuario: "0987654321",
        Code: "EMP002",
        Apellidos: "LOPEZ",
        Nombres: "MARIA ELENA",
        Cargo: "OPERADOR",
        Area: "PRODUCCION",
        Email: "maria.lopez@empresa.com",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla");
    XLSX.writeFile(wb, "plantilla_usuarios_navidad.xlsx");
  };

  const handleClose = () => {
    // Advertir si está procesando
    if (isProcessing) {
      const confirmClose = window.confirm(
        "⚠️ El procesamiento está en curso. ¿Estás seguro de que deseas cerrar? Esto cancelará el proceso."
      );
      if (!confirmClose) {
        return;
      }
    }
    handleClear();
    onClose();
  };

  // Calcular estadísticas
  const getValidationStats = () => {
    if (!validationResults.length) return { total: 0, valid: 0, invalid: 0 };

    const total = validationResults.length;
    const valid = validationResults.filter((r) => r.isValid).length;
    const invalid = total - valid;

    return { total, valid, invalid };
  };

  // Paginación
  const getPaginatedResults = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return validationResults.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(validationResults.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Advertir al cerrar la ventana durante el procesamiento
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isProcessing) {
        e.preventDefault();
        e.returnValue =
          "⚠️ El procesamiento está en curso. ¿Estás seguro de que deseas salir?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isProcessing]);

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl" centered>
      <ModalHeader toggle={isProcessing ? undefined : handleClose}>
        <div className="d-flex align-items-center">
          <FaFileExcel className="me-2 text-primary" />
          Carga de Excel - Usuarios Navidad
        </div>
      </ModalHeader>
      <ModalBody>
        {/* Instrucciones */}
        <Alert color="info" className="mb-4">
          <h6 className="alert-heading">Instrucciones:</h6>
          <ul className="mb-0">
            <li>
              El archivo Excel debe contener las columnas:{" "}
              <strong>"Apellidos"</strong> (requerido) y{" "}
              <strong>"Nombres"</strong> (requerido)
            </li>
            <li>
              Debe tener al menos una de estas columnas:{" "}
              <strong>"Usuario/Cedula"</strong> o <strong>"Code"</strong> (al
              menos una es obligatoria)
            </li>
            <li>
              Columnas opcionales: Cargo, Area, Email, Telefono, Direccion
            </li>
            <li>
              Columnas opcionales: Cargo, Area, Email, Telefono, Direccion
            </li>
            <li>Formato de archivo: .xlsx o .xls</li>
            <li>Tamaño máximo: 5MB</li>
          </ul>
        </Alert>

        {/* Botón de descarga de plantilla */}
        <div className="mb-4">
          <Button
            color="outline-primary"
            onClick={downloadTemplate}
            className="me-2"
          >
            <FaDownload className="me-1" />
            Descargar Plantilla
          </Button>
        </div>

        {/* Zona de carga */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded p-4 text-center ${
            isDragActive ? "border-primary bg-light" : "border-secondary"
          }`}
          style={{
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
        >
          <input {...getInputProps()} />
          <motion.div
            animate={{ scale: isDragActive ? 1.05 : 1 }}
            transition={{ duration: 0.2 }}
          >
            <FaUpload size={32} className="text-muted mb-2" />
            <h6>
              {isDragActive
                ? "Suelta el archivo aquí"
                : "Arrastra y suelta tu archivo Excel aquí"}
            </h6>
            <p className="text-muted mb-0">
              o haz clic para seleccionar un archivo
            </p>
            <small className="text-muted">
              Solo archivos .xlsx, .xls (máx. 5MB)
            </small>
          </motion.div>
        </div>

        {/* Archivo seleccionado */}
        {file && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3"
          >
            <div className="d-flex justify-content-between align-items-center p-3 border rounded">
              <div className="d-flex align-items-center">
                <FaFileExcel className="text-success me-2" size={20} />
                <div>
                  <h6 className="mb-0">{file.name}</h6>
                  <small className="text-muted">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </small>
                </div>
              </div>
              <div>
                {excelData && (
                  <Button
                    color="outline-info"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                    className="me-2"
                  >
                    <FaEye className="me-1" />
                    {showPreview ? "Ocultar" : "Ver"} Vista Previa
                  </Button>
                )}
                <Button color="outline-danger" size="sm" onClick={handleClear}>
                  <FaTrash className="me-1" />
                  Eliminar
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Errores de validación */}
        <AnimatePresence>
          {validationErrors.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3"
            >
              <Alert color="danger">
                <h6 className="alert-heading">
                  <FaExclamationTriangle className="me-2" />
                  Errores de Validación:
                </h6>
                <ul className="mb-0">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Estadísticas de validación */}
        {validationResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3"
          >
            <Row className="mb-3">
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center">
                  <div className="card-body">
                    <h5 className="text-primary mb-1">
                      {getValidationStats().total}
                    </h5>
                    <small className="text-muted">Total Registros</small>
                  </div>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center">
                  <div className="card-body">
                    <h5 className="text-success mb-1">
                      {getValidationStats().valid}
                    </h5>
                    <small className="text-muted">Válidos</small>
                  </div>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center">
                  <div className="card-body">
                    <h5 className="text-danger mb-1">
                      {getValidationStats().invalid}
                    </h5>
                    <small className="text-muted">Con Errores</small>
                  </div>
                </Card>
              </Col>
            </Row>
          </motion.div>
        )}

        {/* Tabla de validación */}
        {showPreview && validationResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3"
          >
            <Card className="border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="mb-0">Resultados de Validación</h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Fila</th>
                        <th>Usuario</th>
                        <th>Código</th>
                        <th>Apellidos</th>
                        <th>Nombres</th>
                        <th>Estado</th>
                        <th>Observaciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedResults().map((result, index) => (
                        <tr
                          key={index}
                          className={
                            result.isValid ? "table-success" : "table-danger"
                          }
                        >
                          <td>{result.rowNumber}</td>
                          <td>
                            {detectedColumns.usuarioColumn &&
                            result.data[detectedColumns.usuarioColumn]
                              ? String(
                                  result.data[detectedColumns.usuarioColumn]
                                ).trim()
                              : "-"}
                          </td>
                          <td>
                            {detectedColumns.codeColumn &&
                            result.data[detectedColumns.codeColumn]
                              ? String(
                                  result.data[detectedColumns.codeColumn]
                                ).trim()
                              : "-"}
                          </td>
                          <td>
                            {detectedColumns.apellidosColumn &&
                            result.data[detectedColumns.apellidosColumn]
                              ? String(
                                  result.data[detectedColumns.apellidosColumn]
                                ).trim()
                              : "-"}
                          </td>
                          <td>
                            {detectedColumns.nombresColumn &&
                            result.data[detectedColumns.nombresColumn]
                              ? String(
                                  result.data[detectedColumns.nombresColumn]
                                ).trim()
                              : "-"}
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                result.isValid ? "bg-success" : "bg-danger"
                              }`}
                            >
                              {result.isValid ? "Válido" : "Error"}
                            </span>
                          </td>
                          <td>
                            <small
                              className={
                                result.isValid ? "text-success" : "text-danger"
                              }
                            >
                              {result.observaciones}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center p-3 border-top">
                    <small className="text-muted">
                      Mostrando {(currentPage - 1) * itemsPerPage + 1} -{" "}
                      {Math.min(
                        currentPage * itemsPerPage,
                        validationResults.length
                      )}{" "}
                      de {validationResults.length} registros
                    </small>
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        color="outline-secondary"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        Primera
                      </Button>
                      <Button
                        color="outline-secondary"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </Button>

                      {/* Páginas inteligentes */}
                      <div className="d-flex gap-1">
                        {(() => {
                          const pages = [];
                          const maxVisiblePages = 5;
                          let startPage = Math.max(
                            1,
                            currentPage - Math.floor(maxVisiblePages / 2)
                          );
                          let endPage = Math.min(
                            totalPages,
                            startPage + maxVisiblePages - 1
                          );

                          // Ajustar si estamos cerca del final
                          if (endPage - startPage + 1 < maxVisiblePages) {
                            startPage = Math.max(
                              1,
                              endPage - maxVisiblePages + 1
                            );
                          }

                          // Primera página si no está visible
                          if (startPage > 1) {
                            pages.push(
                              <Button
                                key={1}
                                color="outline-secondary"
                                size="sm"
                                onClick={() => handlePageChange(1)}
                              >
                                1
                              </Button>
                            );
                            if (startPage > 2) {
                              pages.push(
                                <span
                                  key="ellipsis1"
                                  className="px-2 text-muted"
                                >
                                  ...
                                </span>
                              );
                            }
                          }

                          // Páginas visibles
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <Button
                                key={i}
                                color={
                                  currentPage === i
                                    ? "primary"
                                    : "outline-secondary"
                                }
                                size="sm"
                                onClick={() => handlePageChange(i)}
                              >
                                {i}
                              </Button>
                            );
                          }

                          // Última página si no está visible
                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <span
                                  key="ellipsis2"
                                  className="px-2 text-muted"
                                >
                                  ...
                                </span>
                              );
                            }
                            pages.push(
                              <Button
                                key={totalPages}
                                color="outline-secondary"
                                size="sm"
                                onClick={() => handlePageChange(totalPages)}
                              >
                                {totalPages}
                              </Button>
                            );
                          }

                          return pages;
                        })()}
                      </div>

                      <Button
                        color="outline-secondary"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Siguiente
                      </Button>
                      <Button
                        color="outline-secondary"
                        size="sm"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Última
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Resultado del procesamiento */}
        <AnimatePresence>
          {processResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mt-3"
            >
              <Card className="border-0 shadow-lg">
                <div
                  className="card-header text-white"
                  style={{
                    background:
                      processResult.data?.errors?.length > 0
                        ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                        : processResult.data?.createdUsers > 0
                        ? "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
                        : "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                  }}
                >
                  <div className="d-flex align-items-center">
                    {processResult.data?.errors?.length > 0 ? (
                      <FaExclamationTriangle className="me-2" size={24} />
                    ) : (
                      <FaCheckCircle className="me-2" size={24} />
                    )}
                    <h5 className="mb-0">Procesamiento Completado</h5>
                  </div>
                </div>
                <div className="card-body">
                  {processResult.data && (
                    <>
                      <Row className="mb-3">
                        <Col md={4}>
                          <div className="text-center p-3 border rounded">
                            <div className="d-flex align-items-center justify-content-center mb-2">
                              <FaCheckCircle
                                className="text-success me-2"
                                size={20}
                              />
                              <h4 className="mb-0 text-success">
                                {processResult.data.createdUsers || 0}
                              </h4>
                            </div>
                            <small className="text-muted d-block">
                              Usuarios Creados
                            </small>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="text-center p-3 border rounded">
                            <div className="d-flex align-items-center justify-content-center mb-2">
                              <FaUserMinus
                                className="text-info me-2"
                                size={20}
                              />
                              <h4 className="mb-0 text-info">
                                {processResult.data.skippedUsers || 0}
                              </h4>
                            </div>
                            <small className="text-muted d-block">
                              Omitidos (Ya existían)
                            </small>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="text-center p-3 border rounded">
                            <div className="d-flex align-items-center justify-content-center mb-2">
                              <FaExclamationTriangle
                                className="text-danger me-2"
                                size={20}
                              />
                              <h4 className="mb-0 text-danger">
                                {processResult.data.errors?.length || 0}
                              </h4>
                            </div>
                            <small className="text-muted d-block">
                              Errores
                            </small>
                          </div>
                        </Col>
                      </Row>

                      {processResult.data.errors &&
                        processResult.data.errors.length > 0 && (
                          <div className="mt-3">
                            <Alert color="danger" className="mb-0">
                              <h6 className="alert-heading d-flex align-items-center">
                                <FaExclamationTriangle className="me-2" />
                                Errores Encontrados (
                                {processResult.data.errors.length})
                              </h6>
                              <div
                                className="mt-2"
                                style={{
                                  maxHeight: "300px",
                                  overflowY: "auto",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {processResult.data.errors.map(
                                  (error, index) => {
                                    // Extraer información del error de forma más limpia
                                    let errorMessage = error;
                                    let fila = null;

                                    // Extraer número de fila
                                    const filaMatch =
                                      error.match(/Fila (\d+):/);
                                    if (filaMatch) {
                                      fila = filaMatch[1];
                                    }

                                    // Simplificar mensaje de error
                                    if (
                                      error.includes(
                                        "Argument `code` is missing"
                                      )
                                    ) {
                                      errorMessage = fila
                                        ? `Fila ${fila}: Error - El código no fue proporcionado. Verifica que el archivo Excel tenga la columna "Code" o que el usuario tenga código asignado.`
                                        : "Error - El código no fue proporcionado";
                                    } else if (
                                      error.includes("Invalid `prisma")
                                    ) {
                                      // Extraer solo la parte relevante del error
                                      if (
                                        error.includes(
                                          "Argument `code` is missing"
                                        )
                                      ) {
                                        errorMessage = fila
                                          ? `Fila ${fila}: Error - El código es requerido pero no fue proporcionado`
                                          : "Error - El código es requerido";
                                      } else {
                                        const parts = error.split(":");
                                        if (parts.length > 0 && fila) {
                                          errorMessage = `Fila ${fila}: ${parts[
                                            parts.length - 1
                                          ].trim()}`;
                                        }
                                      }
                                    }

                                    return (
                                      <div
                                        key={index}
                                        className="mb-2 p-2 bg-light rounded border-start border-danger border-3"
                                      >
                                        <div className="d-flex align-items-start">
                                          <FaExclamationTriangle
                                            className="text-danger me-2 mt-1"
                                            size={14}
                                          />
                                          <span className="text-danger">
                                            {errorMessage}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </Alert>
                          </div>
                        )}
                    </>
                  )}
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Indicador de validación */}
        {isValidating && (
          <div className="mt-3">
            <div className="d-flex align-items-center">
              <Spinner size="sm" className="me-2" />
              <span>Validando archivo...</span>
            </div>
            <Progress value={100} animated color="info" className="mt-2" />
          </div>
        )}

        {/* Indicador de procesamiento con progreso */}
        {isProcessing && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3"
          >
            <Card
              className="border-0 shadow-lg"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              <div className="card-body text-white">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h5 className="mb-1">
                      <Spinner size="sm" className="me-2" />
                      Procesando usuarios...
                    </h5>
                    {processingProgress.currentUser && (
                      <p className="mb-0 small">
                        Procesando:{" "}
                        <strong>{processingProgress.currentUser}</strong>
                      </p>
                    )}
                  </div>
                  <div className="text-end">
                    <h4 className="mb-0">
                      {processingProgress.current} / {processingProgress.total}
                    </h4>
                    <small className="opacity-75">
                      {processingProgress.total > 0
                        ? Math.round(
                            (processingProgress.current /
                              processingProgress.total) *
                              100
                          )
                        : 0}
                      %
                    </small>
                  </div>
                </div>

                <div className="mb-3 position-relative">
                  {processingProgress.total > 0 ? (
                    <>
                      <div
                        style={{
                          height: "35px",
                          backgroundColor: "rgba(255, 255, 255, 0.2)",
                          borderRadius: "17px",
                          overflow: "hidden",
                          position: "relative",
                          border: "2px solid rgba(255, 255, 255, 0.3)",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.min(
                              100,
                              (processingProgress.current /
                                processingProgress.total) *
                                100
                            )}%`,
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            transition: "width 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            color: "#667eea",
                            fontSize: "15px",
                            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
                          }}
                        >
                          {Math.round(
                            (processingProgress.current /
                              processingProgress.total) *
                              100
                          )}
                          %
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        height: "35px",
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "17px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                      }}
                    >
                      Iniciando...
                    </div>
                  )}
                </div>

                <Row className="text-center">
                  <Col md={4}>
                    <div className="p-2 bg-white bg-opacity-20 rounded">
                      <h6 className="mb-0">{processingProgress.success}</h6>
                      <small className="opacity-75">Creados</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-2 bg-white bg-opacity-20 rounded">
                      <h6 className="mb-0">{processingProgress.errors}</h6>
                      <small className="opacity-75">Errores</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-2 bg-white bg-opacity-20 rounded">
                      <h6 className="mb-0">
                        {processingProgress.total - processingProgress.current}
                      </h6>
                      <small className="opacity-75">Pendientes</small>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Opciones de procesamiento */}
        {excelData && validationResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3"
          >
            <Card className="border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="mb-0 d-flex align-items-center">
                  <FaUserMinus className="me-2" />
                  Configuración de Procesamiento
                </h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">
                      <strong>Año de Navidad *</strong>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={año}
                      onChange={(e) => setAño(parseInt(e.target.value))}
                      min="2020"
                      max="2030"
                      required
                      disabled={isProcessing}
                    />
                    <small className="text-muted">
                      Año para el cual se procesarán los usuarios navidad
                    </small>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="outline-secondary"
          onClick={handleClose}
          disabled={isProcessing}
        >
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={handleProcessFile}
          disabled={
            !excelData ||
            validationErrors.length > 0 ||
            isProcessing ||
            getValidationStats().valid === 0 ||
            !año
          }
          className="d-flex align-items-center"
        >
          {isProcessing ? (
            <>
              <Spinner size="sm" className="me-2" />
              Procesando...
            </>
          ) : (
            <>
              <FaCheckCircle className="me-2" />
              Procesar Archivo ({getValidationStats().valid} válidos)
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExcelUploadModal;
